import * as React from 'react';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { CashLot } from '../api/cash-lot';
import { getAggregatedCashLotItems } from './shared';

interface WorkingCapitalItem {
  ar: number;
  ap: number;
  trend: number;
  dateUnit: string;
}

const getItems = (cashLot: CashLot, frequency: string): WorkingCapitalItem[] => {
  const aggregatedItems = getAggregatedCashLotItems(cashLot.items.filter(x => ['AR', 'AP'].includes(x.source) && x.type === 'Payment'), frequency);
  const workingCapitalItems: WorkingCapitalItem[] = [];

  for (let aggregatedItem of aggregatedItems) {
    let ar = 0;
    let ap = 0;

    for (let cashLotItem of aggregatedItem.items) {
      switch (cashLotItem.source) {
        case 'AR': ar += cashLotItem.amount; break;
        case 'AP': ap += cashLotItem.amount; break;
        default: break;
      }
    }

    workingCapitalItems.push({
      ar: ar,
      ap: -ap,
      trend: (ar-ap),
      dateUnit: aggregatedItem.dateUnit
    })
  }

  return workingCapitalItems;
}

const getHighchartOptions = (workingCapitalItems: WorkingCapitalItem[]): Highcharts.Options => {
  return {    
    chart: {
      height: 250
    },
    title: {
      text: ''
    },
    xAxis: {
      categories: workingCapitalItems.map(x => x.dateUnit)
    },
    yAxis: {
      title: {
        text: ''
      }
    },
    plotOptions: {
      column: {
        stacking: 'normal',
        dataLabels: {
          enabled: false
        }
      }
    },
    colors: ['#3fb1e3', '#3893be', '#2e6586'],
    tooltip: {
      formatter: function (tooltip) {
        let yValue = this.point.y;
        
        if(this.series.name === 'AP')
        {          
          yValue = Math.abs(this.point.y);          
        }        
        
        return '<span style="font-size: 10px">' + this.point.category + '</span><br/>' +
                '<span style="color:' + this.point.color + '">●</span> ' + this.series.name + ': <b>'+Highcharts.numberFormat (yValue, 2, ".", "," )+'</b><br/>';
      }
    },
    series: [
      {
        type: 'column',
        name: 'AP',
        data: workingCapitalItems.map(x => x.ap)
      },
      {
        type: 'column',
        name: 'AR',
        data: workingCapitalItems.map(x => x.ar)
      },
      {
        type: 'spline',
        name: 'Trend',
        data: workingCapitalItems.map(x => x.trend),
        color: Highcharts.getOptions().colors[3],
        marker: {
          lineWidth: 2,
          lineColor: Highcharts.getOptions().colors[3],
          fillColor: 'white'
        }
      }
    ],
    credits:{
      enabled: false
    }
  }
};

export const WorkingCapital = ({ cashLot, frequency }) => {
  const workingCapitalItems = React.useMemo(() => getItems(cashLot, frequency), [cashLot.items['_changeToken'], frequency]);

  const options = getHighchartOptions(workingCapitalItems);

  return (
    <HighchartsReact highcharts={Highcharts} options={options} />
  );
};
